import { Button, Icon, Table, Tag } from '@dev-spendesk/grapes';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { type AccountingSoftware } from 'modules/bookkeep/integration/status';
import { type AnalyticalFieldMappingV2 } from 'modules/bookkeep/settings/accounting';
import { routeFor, routes } from 'src/core/constants/routes';
import { type AnalyticalFieldExportSettings } from 'src/core/modules/accounting-integration/apis/useAnalyticalFieldExportSettings';
import { type SyncSetting } from 'src/core/modules/accounting-integration/apis/useGetSyncSettings';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { type CustomField } from '../../../../../hooks/useCustomFieldsQuery';

interface Props {
  integration: AccountingSoftware;
  customFields: CustomField[];
  analyticalFieldMapping: AnalyticalFieldMappingV2[];
  exportConfig: AnalyticalFieldExportSettings[];
  isCostCenterEnabled: boolean;
  allSyncSettings: SyncSetting[];
}

type AnalyticalFieldTableRow = {
  internalName: string;
  externalName: string;
  externalId: string;
  internalId: string;
  fieldType: 'customField' | 'costCenter';
  exportConfig?: AnalyticalFieldExportSettings;
  syncMode:
    | 'push'
    | 'none'
    | 'pull'
    | 'pullAndMap'
    | 'bidirectional'
    | 'notConfigured';
};

export const StaticMapAndPullAndMapTableMapSync = ({
  integration,
  customFields,
  analyticalFieldMapping,
  exportConfig,
  isCostCenterEnabled,
  allSyncSettings,
}: Props) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();

  const fieldsToDisplay = [
    ...(isCostCenterEnabled
      ? [
          {
            id: 'costCenter',
            internalId: 'costCenter',
            name: t(
              'bookkeep.integrations.settings.analyticalFieldsMappingsTable.costCenters',
            ),
            type: 'costCenter' as const,
          },
        ]
      : []),
    ...customFields
      .filter((customField) => !customField.deleted_at)
      .map((customField) => ({
        ...customField,
        type: 'customField' as const,
      })),
  ];

  const tableRows: AnalyticalFieldTableRow[] = fieldsToDisplay.map((field) => {
    const mapping = analyticalFieldMapping.find(
      (m) => m.internalId === field.id,
    );

    const fieldExportConfig: AnalyticalFieldExportSettings | undefined =
      mapping?.externalId.includes('customSegment')
        ? {
            sendOnHeader: true,
            sendOnLine: true,
            externalId: mapping.externalId,
          }
        : exportConfig.find(
            (config) => config.externalId === mapping?.externalId,
          );

    const syncSetting = allSyncSettings.find(
      (setting) => setting.fieldId === mapping?.internalId,
    );

    return {
      internalName: field.name,
      externalName: mapping?.externalName || '',
      externalId: mapping?.externalId || '',
      internalId: field.id,
      fieldType: field.type,
      exportConfig: fieldExportConfig,
      syncMode: syncSetting?.syncMode ?? 'notConfigured',
    };
  });

  return (
    <Table<AnalyticalFieldTableRow>
      rowHeight="compact"
      columns={[
        {
          id: 'spendesk-field-name',
          header: t(
            'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.spendeskField',
          ),
          renderCell(row) {
            return row.internalName;
          },
        },
        {
          id: 'analytical-field-sync-configuration',
          header: t(
            'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.syncSetting',
            { integration },
          ),
          renderCell(row) {
            if (row.syncMode === 'notConfigured') {
              return (
                <div className="text-content-secondary-bg-primary flex items-center gap-8">
                  <Icon name="hexagone-cross" />
                  {t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.notConfigured',
                  )}
                </div>
              );
            }

            if (row.syncMode === 'none') {
              return (
                <Tag variant="carbon" iconName="hexagone-cross">
                  {t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.sync.none',
                    { integration },
                  )}
                </Tag>
              );
            }

            if (row.syncMode === 'pull') {
              return (
                <Tag variant="carbon" iconName="arrow-uturn-left-down">
                  {t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.sync.pull',
                    { integration },
                  )}
                </Tag>
              );
            }

            if (row.syncMode === 'pullAndMap') {
              return (
                <Tag variant="carbon" iconName="link">
                  {t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.sync.pullAndMap',
                    { integration },
                  )}
                </Tag>
              );
            }

            return row.internalName;
          },
        },
        {
          id: 'analytical-field-external-name',
          header: t(
            'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.mappedField',
            { integration },
          ),
          renderCell(row) {
            if (row.syncMode === 'none') {
              return (
                <span className="text-content-secondary-bg-primary">
                  {t('misc.na')}
                </span>
              );
            }

            if (!row.externalName) {
              return (
                <div className="text-content-secondary-bg-primary flex items-center gap-8">
                  <Icon name="hexagone-cross" />
                  {t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.notMapped',
                  )}
                </div>
              );
            }

            return (
              <Tag variant="carbon" iconName="link">
                {row.externalName}
              </Tag>
            );
          },
        },
        ...(integration === 'Netsuite'
          ? [
              {
                id: 'analytical-field-export-configuration',
                header: t(
                  'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.exportConfig.title',
                ),
                renderCell(row: AnalyticalFieldTableRow) {
                  if (row.syncMode === 'none') {
                    return (
                      <span className="text-content-secondary-bg-primary">
                        {t('misc.na')}
                      </span>
                    );
                  }

                  if (row.externalId === 'expenseCategory') {
                    return (
                      <Tag variant="carbon">
                        {t(
                          'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.exportConfig.line',
                        )}
                      </Tag>
                    );
                  }

                  if (!row.exportConfig) {
                    return (
                      <div className="text-content-secondary-bg-primary flex items-center gap-8">
                        <Icon name="hexagone-cross" />
                        {t(
                          'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.notConfigured',
                        )}
                      </div>
                    );
                  }

                  return (
                    <>
                      {row.exportConfig.sendOnHeader && (
                        <>
                          <Tag variant="carbon">
                            {t(
                              'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.exportConfig.body',
                            )}
                          </Tag>{' '}
                        </>
                      )}
                      {row.exportConfig.sendOnLine && (
                        <Tag variant="carbon">
                          {t(
                            'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.exportConfig.line',
                          )}
                        </Tag>
                      )}
                    </>
                  );
                },
              },
            ]
          : []),
        {
          id: 'analytical-field-see-details',
          header: t(
            'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.link',
          ),
          renderCell(row) {
            if (row.internalId === 'costCenter') {
              return (
                <Button
                  className="p-0 underline"
                  variant="tertiaryNeutral"
                  iconName="arrow-top-right-square"
                  text={t(
                    'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.link',
                  )}
                  onClick={() => {
                    history.push({
                      pathname: routeFor(routes.COST_CENTERS.path, {
                        company: companyId,
                      }),
                    });
                  }}
                />
              );
            }
            return (
              <Button
                component="a"
                href={routeFor(routes.COMPANY_CATEGORIES.path, {
                  company: companyId,
                })}
                className="p-0 underline"
                variant="tertiaryNeutral"
                iconName="arrow-top-right-square"
                text={t(
                  'bookkeep.integrations.settings.analyticalFieldsMappingsTable.mapSyncTable.link',
                )}
              />
            );
          },
        },
      ]}
      data={tableRows}
      footer={
        tableRows.length === 0 ? (
          <TableFooter analyticalFieldMapping={tableRows} />
        ) : undefined
      }
    />
  );
};

const TableFooter = ({
  analyticalFieldMapping,
}: {
  analyticalFieldMapping: AnalyticalFieldTableRow[];
}) => {
  const { t } = useTranslation('global');

  if (analyticalFieldMapping.length === 0) {
    return (
      <em>
        {t(
          'bookkeep.integrations.settings.analyticalFieldsMappingsTable.noAnalyticalFields',
        )}
      </em>
    );
  }

  return null;
};
